import { SpreadGroundImage } from "editor/src/store/design/types";

// GAPI-30848 — strip the multi-tech suffix from a spread name so we can
// fall back to the catalogue's canonical-area scene when an exact match
// for the tech-suffixed name isn't found. Bella+Canvas 8800's catalogue
// ships editor scenes only under one tech per area (typically the
// default DTF — e.g. `front-dtf`/`back-dtf` or bare `front`/`back`);
// the iframe's multi-tech spreads use tech-suffixed names. Without the
// fallback, scenes for any non-default-tech spread render as white.
const MULTI_TECH_SUFFIX_RE = /-(dtg|dtf|embroidery|emb|sublimation)$/i;

const stripTech = (name: string): string =>
  name.replace(MULTI_TECH_SUFFIX_RE, "");

/**
 * GAPI-30848 — locate a spread ground image (background OR foreground) for
 * a given spread name with a tech-suffix-insensitive fallback.
 *
 *   1. Exact name match (`front-dtg` → `front-dtg`) — original behavior.
 *   2. Tech-suffix-insensitive match (`front-dtg` → `front-dtf` or `front`)
 *      — same physical garment scene; tech only changes the print process,
 *      not the visual.
 *
 * Used by the editor canvas (`getSpreadBackgroundImage`), the per-spread
 * preview hook (`useSpreadPreview`), and the grouped-spread thumbnail
 * helper (`getGroupedSpreadInfo`). Without this fallback, multi-tech
 * spreads render with no garment background — the customer sees the
 * design on a white card instead of on the tank top.
 */
export function findSpreadGroundImage(
  images: SpreadGroundImage[] | undefined | null,
  spreadName: string | undefined | null,
): SpreadGroundImage | undefined {
  if (!images?.length || !spreadName) return undefined;
  const exact = images.find((image) => image?.name === spreadName);
  if (exact) return exact;
  const bareTarget = stripTech(spreadName);
  if (bareTarget === spreadName) return undefined;
  return images.find(
    (image) => image?.name && stripTech(image.name) === bareTarget,
  );
}

export default findSpreadGroundImage;
